import { useAnalyticsJobs } from "hooks/useJobs";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { analyticsRefreshEnded, followRefreshJob } from "store/analyticsStore";
import type { Job } from "store/jobsStore";
import { ProcessingJobStatus } from "store/jobsStore";
import { fetchCompanyInfo } from "store/settingsStore";
import { useAppDispatch, useAppSelector } from "store/store";

/**
 * Follows the company's analytics refresh job, whoever started it: the data is rewritten chunk by
 * chunk meanwhile, so the analytics page shows the progress instead of reading half-refreshed figures.
 * The followed job (started here or seen running) lives in the store, so it is still caught when it
 * ends after the page was left. Then the tabs reload (success) or the editor is told to start again.
 */
/**
 * A refresh stamps its job after each chunk (a few minutes at most): one silent for longer was left
 * PROCESSING by a dead server instance. The page is not blocked by it; the server fails it on the same
 * threshold (fail-stale-analytics-refresh cron), after which a new refresh can start.
 */
const STALE_JOB_MS = 15 * 60_000;

const isStaleJob = (job: Job) => Date.now() - new Date(job.modifiedAt ?? job.createdAt).getTime() > STALE_JOB_MS;

export function useAnalyticsRefreshJob() {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { analyticsJobs, analyticsJobsStatus } = useAppSelector((state) => state.jobs);
    const { followedRefreshJobId } = useAppSelector((state) => state.analytics);
    useAnalyticsJobs(true);

    const runningJob = analyticsJobs.jobs.find(
        (job) => job.status === ProcessingJobStatus.PROCESSING && !isStaleJob(job),
    );

    useEffect(() => {
        if (runningJob) {
            if (runningJob.id !== followedRefreshJobId) dispatch(followRefreshJob(runningJob.id));
            return;
        }

        // Absent from a list fetched before the job was opened: wait for the next poll
        const endedJob = analyticsJobs.jobs.find((job) => job.id === followedRefreshJobId);
        if (!endedJob) return;

        const succeeded = endedJob.status === ProcessingJobStatus.DONE;
        dispatch(analyticsRefreshEnded(succeeded));
        // Failed, or still PROCESSING but stale: its instance died, the data is left half refreshed
        if (!succeeded) {
            toast.error(`${t("Analytics.RefreshFailed")}`, { toastId: "Analytics.RefreshFailed" });
        }
        // Refresh button state and tooltip (last refresh date, failure)
        dispatch(fetchCompanyInfo());
    }, [analyticsJobs.jobs, runningJob, followedRefreshJobId, dispatch, t]);

    return {
        // Until the first answer, a refresh may be running: do not start reading the data yet
        isLoaded: analyticsJobsStatus !== "idle",
        runningJob,
    };
}
